import moment from './moment-enhanced.js';

export const convert_localMomentObj_to_usertime = (momentObject, userTimezone) => {
  /*
  This function converts a momentObject into another momentObject with
  shifted time so that the user can edit the momentObject in his
  specified userTimezone. It is meant as a workaround becase date-picker
  (or more specifially 'new Date') always converts to the browser
  timezone.

  FAQ:

  Why can't moment handle the conversion?
  ---
  It can, but moment.tz will just add a timezone to it's internal state
  and use it whenever .format() is called, it actually won't update
  the internal timestamp since it's UTC based. The problem is that
  the Element date-picker internally uses a JavaScript 'Date', which
  does not care about the extra timezone field.

  Why not use 'moment().utcOffset()' instead of 'getTimezoneOffset'?
  ---
  The moment utcOffset can be changed with moment.tz.setDefault(...),
  which is nice but then obviously utcOffset() will return another value.
  However since Element date-picker internally uses a JavaScript 'Date'
  we would correct with an offset only moment knows about but not the
  JavaScript 'Date'. This is why we need the browsers offset, which we
  can get with getTimezoneOffset().

  ( Also it is dangerous, because it will only set the Timezone on the
    local moment which has been imported in every Vue file. )

  Why are the two differences added?
  ---
  This is because the two functions measure the difference differently:

  > moment().utcOffset()
  > -300
  > new Date().getTimezoneOffset()
  > 300

  See also moment Docs: "Unlike moment.fn.zone this function returns
  the real offset from UTC, not the reverse offset (as returned by
  Date.prototype.getTimezoneOffset)."

  Why convert to 'UTC'?
  ---
  Actually tz('UTC') just sets the internal timezone to 'UTC' and does
  not convert the internal timestamp. This 'removes' the timezone
  information, but it will get deleted anyways when converted to
  'Date'. It's not necessary, but at least we remove confusing
  information.

  Why can we completly ignore the timezone of the moment object?
  ---
  While moment.tz('2018-05-12T23:00Z', 'Pacific/Tahiti') will create a moment
  object with UTC timestamp 1526166000 and an attached timezone field, that says
  'Pacific/Tahiti', it would be intuitive to assume, that the timezone field
  should be used to display and edit the UTC timestamp in this specific timezone.

  On the other hand, this is not really relevant for the user input, since we
  only really care about the prefered timezone of the user (which should be
  passed userTimezone).

  However, if the timezone of the moment object should be used it can easly be
  done with:
  > convert_localMomentObj_to_usertime( momentObj, momentObj.tz() )

  Further discussion: Maybe this should be the default, when no second argument
  is given. (See also: convert_Timestamp_to_calendartime)

  This seems like a lot of hassle to support custom timezones...
  ---
  Totally... especially since most operating systems and browser offer built-in
  timezone support and automatically convert between UTC and the users timezone
  🙄

  */

  let time_difference_browsertime_to_UTC_in_minutes = new Date().getTimezoneOffset();
  let time_difference_usertime_to_UTC_in_minutes = moment.tz(userTimezone).utcOffset();

  let utcOffset = time_difference_browsertime_to_UTC_in_minutes
                + time_difference_usertime_to_UTC_in_minutes;

  return momentObject
    .clone()
    .tz('UTC')
    .add(utcOffset, 'minutes');
}

export const convert_usertime_to_localMomentObj = (usertime, userTimezone) => {
  /*
  This is the reverse of convert_localMomentObj_to_usertime. It converts a
  user input from a date-picker back to a clean moment object with timezone.

  FAQ see convert_localMomentObj_to_usertime.
  */

  let time_difference_browsertime_to_UTC_in_minutes = new Date().getTimezoneOffset();
  let time_difference_usertime_to_UTC_in_minutes = moment.tz(userTimezone).utcOffset();

  let utcOffset = time_difference_browsertime_to_UTC_in_minutes
                + time_difference_usertime_to_UTC_in_minutes;

  return moment( usertime )
    .subtract(utcOffset, 'minutes')
    .tz(userTimezone);
}

export const convert_Timestamp_to_calendartime = (timestamp, userTimezone) => {
  /*
  This function converts a timestmap into a moment object, so that it can be
  used with fullcalendar for the current the user timezone.

  It is meant as a workaround becase fullcalendar does not care about the
  additional timezone information in the moment object

  FAQ:

  Why does the function not call tz, when userTimezone is not set?
  ---
  Calling .tz() would create a moment object with "timezone capabilities".
  If no userTimezone is given, it means THAT we want to use the browser's timezone.
  While moment.tz.guess() can give us a guess (for the timezone name, not offset!),
  why not just fallback to the browsers capability to access the users timezone.
  We don't need the name anyways, just make it a local time object.

  This looks like a identity function...
  ---
  We said fullcalendar will ignore the tz info, so why set it?
  moment.unix(1545678900).tz('Europe/Berlin').unix() returns 15456789000
  TODO!

  Why not 'convert_localMomentObj_to_calendartime'?
  ---
  Just do convert_Timestamp_to_calendartime( myMomentObj.unix(),... )

  */

  // TODO: Check if valid Timestamp
  // TODO: Support firebase timestamp

  return userTimezone
    ? moment.unix(timestamp).tz(userTimezone)
    : moment.unix(timestamp);
}

export const convert_calendartime_to_localMomentObj = (calendarMomentObject, userTimezone) => {
  /*
  This is the reverse of convert_Timestamp_to_calendartime. It is dealing
  with the conversion of fullcalendar moment objects back to valid local
  moment object with a timezone attached to it.

  It is meant as a workaround becase fullcalendar does not care about the
  additional timezone information in the moment object

  FAQ:
  Why can we ignore the browser time here (see convert_UTC_time_to_usertime)?
  ---
  Because fullcalendar interally uses moment objects (until v3, not in v4!) and
  does not have the same problem as the date-picker. To distinguish the different
  moment objects, the function results are called 'usertime' and 'calendartime'.

  If fullCalendar interally uses moment, why do we need to convert then?
  ---
  Because fullCalendar does not use moment.tz, it just uses moment.

  Why not make it more consistent and return a unix timestamp?
  ---
  We could do that, but then all the information about the userTimezone is lost,
  which means we can't use the momentObject anywhere else. Maybe there is other
  applications then just generating a unix timestamp for the database.
  Also, the conversion is straight forward and can be achieved by:
  > localMomentObject.unix()
  */

  let time_difference_usertime_to_UTC_in_minutes = moment.tz(userTimezone).utcOffset();

  // console.log("userTimezone", userTimezone);
  // console.log("time_difference_usertime_to_UTC_in_minutes", time_difference_usertime_to_UTC_in_minutes);

  return calendarMomentObject
    .clone()
    .subtract(time_difference_usertime_to_UTC_in_minutes, 'minutes')
    .tz(userTimezone);
}

export const convert_anything_to_localMomentObj = (timestamp, userTimezone) => {
  // TODO: We might need this function
}

export const convert_timezoneNeutral_to_qualifiedMomentObj = (momentObj, userTimezone) => {

  /* This funcion is used to convert 'timezone neutral' moment object, that are
  used to describe timeslots in timeslot collection, into 'qualified moment object',
  which means that the current user timezone gets 'appended' making the moment
  object timezone specific or 'timezoned'. */

  // moment.parseZone() ?

  /* Get utc formated ISO string like "2018-12-09T03:41:19Z" and remove last character 'Z' */
  let isoString = momentObj.clone().utc().format();
  let timezoneNeutralIsoString = isoString.substring(0, isoString.length - 1)

  /* Append the current user timezone to the "timezone neutral" datetime, so that
      '2018-05-06T14:00'
             +
      'America/New_York'
          becomes
      '2018-05-06T14:00:00-04:00'.
  */
  return moment.tz(timezoneNeutralIsoString, userTimezone);
}
